import { SQLITE3MT_CLIENT_BUSY_TIMEOUT_MS } from './sqliteBusyPolicy'

export type SqlitePragmaRole = 'runtime-owner' | 'client' | 'checkpoint'

export interface SqlitePragmaOptions {
    role: SqlitePragmaRole
    pageSize: number
    exec: (sql: string) => void
    getPageCount?: () => number
    bootstrapStoragePragmas?: boolean
}

export interface EnsureSqliteWalModeOptions {
    role: SqlitePragmaRole
    getJournalMode: () => string | null
    setJournalModeToWal?: () => string | null
    bootstrapStoragePragmas?: boolean
}

/**
 * Apply connection-role-specific SQLite pragmas.
 *
 * Only the runtime-owner is allowed to bootstrap storage-level settings on a
 * fresh database. Client and checkpoint handles attach to an already-owned
 * runtime and should stay on connection-local settings.
 */
export function applySqlitePragmas(options: SqlitePragmaOptions): void {
    const { role, pageSize, exec, getPageCount, bootstrapStoragePragmas = false } = options
    const shouldBootstrapStorage = role === 'runtime-owner' || (role === 'client' && bootstrapStoragePragmas)

    if (shouldBootstrapStorage) {
        if (pageSize > 0 && (getPageCount?.() ?? 0) === 0) {
            exec(`PRAGMA page_size = ${pageSize}`)
        }
        exec('PRAGMA journal_mode = WAL')
        exec('PRAGMA synchronous = NORMAL')
        exec('PRAGMA foreign_keys = ON')
        exec(`PRAGMA busy_timeout = ${SQLITE3MT_CLIENT_BUSY_TIMEOUT_MS}`)
        exec('PRAGMA wal_autocheckpoint = 0')
        return
    }

    if (role === 'client') {
        exec('PRAGMA synchronous = NORMAL')
        exec('PRAGMA foreign_keys = ON')
        exec(`PRAGMA busy_timeout = ${SQLITE3MT_CLIENT_BUSY_TIMEOUT_MS}`)
        exec('PRAGMA wal_autocheckpoint = 0')
        return
    }

    exec('PRAGMA wal_autocheckpoint = 0')
}

function normalizeJournalMode(mode: string | null | undefined): string | null {
    const normalized = mode?.trim().toLowerCase()
    return normalized != null && normalized.length > 0 ? normalized : null
}

export function ensureSqliteWalMode(options: EnsureSqliteWalModeOptions): void {
    const {
        role,
        getJournalMode,
        setJournalModeToWal,
        bootstrapStoragePragmas = false,
    } = options
    const shouldBootstrapStorage = role === 'runtime-owner' || (role === 'client' && bootstrapStoragePragmas)

    let journalMode = normalizeJournalMode(getJournalMode())
    if (journalMode === 'wal') {
        return
    }

    if (shouldBootstrapStorage && setJournalModeToWal != null) {
        journalMode = normalizeJournalMode(setJournalModeToWal())
        if (journalMode === 'wal') {
            return
        }

        journalMode = normalizeJournalMode(getJournalMode())
        if (journalMode === 'wal') {
            return
        }
    }

    throw new Error(`sqlite3mt requires journal_mode=WAL for ${role} handles, got ${journalMode ?? 'unknown'}`)
}
